<script setup>
import { ref, toRefs, onBeforeMount, onMounted } from 'vue'
import mySwal from '@/src/javascripts/plugins/mySwal.js'
import { SendOutlined, FileDoneOutlined } from '@ant-design/icons-vue'
import BaseFileUpload from '@/src/vueComponent/BaseFileUpload.vue'
import BackButton from '@/src/vueComponent/BackButton.vue'

const props = defineProps({
  token: {
    type: String
  },
  companyType: {
    type: String
  },
  shippingReport: {
    type: Object
  },
  qualityFeedback: {
    type: Object
  },
  applicant: {
    type: Object
  }
})
const { token, companyType, shippingReport, qualityFeedback, applicant } = toRefs(props)
const caseStatus = ref('')
const feedbackResponse = ref({})
const records = ref([])

onBeforeMount(() => {
  if (qualityFeedback.value.id) {
    feedbackResponse.value = {
      applicant: `${qualityFeedback.value.applicant.company} ${qualityFeedback.value.applicant.name}`,
      feedback_code: qualityFeedback.value.feedback_code,
      lot_no: qualityFeedback.value.lot_no,
      product: qualityFeedback.value.product,
      material_report_id: qualityFeedback.value.product,
      response: {
        content: '',
        upload_files: []
      }
    }
  } else {
    feedbackResponse.value = {
      applicant: `${applicant.value.company} ${applicant.value.name}`,
      feedback_code: '新單號',
      lot_no: shippingReport.value.lot_no,
      product: shippingReport.value.product,
      serial_number: shippingReport.value.serial_number,
      prod_date: shippingReport.value.prod_date,
      response: {
        content: '',
        upload_files: []
      }
    }

    if (companyType.value === 'Customer') {
      feedbackResponse.value.shipping_report_id = shippingReport.value.lot_no
    }
  }

  records.value = qualityFeedback.value.feedback_details
  caseStatus.value = qualityFeedback.value.state
})

const scrollToBottomTable = ref(null)
onMounted(() => {
  const { id } = qualityFeedback.value
  const currentFeedbackDetailsLength = qualityFeedback.value.feedback_details?.length
  const qualityFeedbackId = JSON.parse(localStorage.getItem('qualityFeedbackId'))
  const recordLength = JSON.parse(localStorage.getItem('recordLength'))

  if (id !== null && id !== undefined && id !== qualityFeedbackId) {
    localStorage.setItem('qualityFeedbackId', id)
    localStorage.setItem('recordLength', currentFeedbackDetailsLength)
  } else if (id && id === qualityFeedbackId && currentFeedbackDetailsLength > recordLength) {
    scrollToBottomTable.value.scrollTo({
      top: scrollToBottomTable.value.scrollHeight,
      behavior: 'smooth'
    })
    localStorage.setItem('recordLength', currentFeedbackDetailsLength)
  }
})

const handleDeletePreviewFile = (fileIndex) => {
  feedbackResponse.value = {
    ...feedbackResponse.value,
    response: {
      ...feedbackResponse.value.response,
      upload_files: [
        ...feedbackResponse.value.response.upload_files.slice(0, fileIndex),
        ...feedbackResponse.value.response.upload_files.slice(fileIndex + 1)
      ]
    }
  }
}

const determinePartnerUrl = () => {
  if (companyType.value === 'Customer') {
    return '/customer/quality_feedbacks'
  } else if (companyType.value === 'Supplier') {
    return '/supplier/quality_feedbacks'
  }
}

const submitForm = () => {
  const formData = new FormData()
  for (const [key, value] of Object.entries(feedbackResponse.value)) {
    if (typeof value !== 'object') {
      formData.append(key, value)
    }
  }

  formData.append('response[content]', feedbackResponse.value.response.content)

  feedbackResponse.value.response.upload_files.forEach((file, index) => {
    if (file instanceof File) {
      formData.append(`upload_files[${index}]`, file)
    }
  })

  const partnerUrl = determinePartnerUrl()
  $.ajax({
    url: qualityFeedback.value.id
      ? `${partnerUrl}/${qualityFeedback.value.id}/feedback`
      : partnerUrl,
    type: qualityFeedback.value.id ? 'PATCH' : 'POST',
    dataType: 'json',
    headers: { 'X-CSRF-Token': token.value },
    authenticity_token: true,
    processData: false,
    contentType: false,
    enctype: 'multipart/form-data',
    data: formData,
    success: async (response) => {
      if (response.state === 0) {
        mySwal
          .success({
            title: response.message
          })
          .then(() => {
            $(document).off('turbo:before-visit')
            if (!qualityFeedback.value.id) {
              Turbo.visit(partnerUrl)
            } else {
              Turbo.visit(`${partnerUrl}/${qualityFeedback.value.id}`)
            }
          })
      }
    },
    error: async (error) => {
      console.log('error', error)
      if (error.responseJSON.state === -1) {
        await mySwal.error({
          title: error.responseJSON.message.join('<br />'),
          timer: 3000
        })

        if (error.responseJSON.redirect_to) {
          window.location.replace(error.responseJSON.redirect_to)
        }
      }
    }
  })
}

const closeCase = async () => {
  const isConfirm = await mySwal.confirm({
    title: '是否確定結案？',
    confirmButtonText: '是',
    cancelButtonText: '否',
    confirmButtonColor: '#0e3884'
  })

  if (!isConfirm) {
    return
  }

  const partnerUrl = determinePartnerUrl()
  $.ajax({
    url: `${partnerUrl}/${qualityFeedback.value.id}/close`,
    type: 'POST',
    dataType: 'json',
    headers: { 'X-CSRF-Token': token.value },
    authenticity_token: true,
    success: async (response) => {
      if (response.state === 0) {
        mySwal
          .success({
            title: response.message
          })
          .then(() => {
            $(document).off('turbo:before-visit')
            Turbo.visit(`${partnerUrl}/${qualityFeedback.value.id}`)
          })
      }
    },
    error: (error) => {
      console.log('error', error)
      if (error.responseJSON.state === -1) {
        mySwal.error({
          title: error.responseJSON.message.join('<br />')
        })
      }
    }
  })
}
</script>
<template>
  <div>
    <div class="my-2 flex items-center justify-between py-2">
      <h3 v-if="qualityFeedback.id" class="py-2.5 text-2xl font-bold">品質回報</h3>
      <h3 v-else class="text-2xl font-bold">新增品質回報</h3>
      <button
        v-if="qualityFeedback.id && qualityFeedback.display_closed_button"
        @click="closeCase()"
        type="button"
        class="btn btn-warning text-xl"
      >
        <FileDoneOutlined />
        結案
      </button>
    </div>
    <div class="mb-4 grid grid-cols-4 rounded-md border border-gray-200 xl:grid-cols-2">
      <div class="rounded-none border border-l-0 border-t-0 border-gray-200 p-4">
        <label class="mb-2 block text-xl font-bold">發起人</label>
        <div>
          {{ feedbackResponse.applicant }}
        </div>
      </div>
      <div class="rounded-none border border-l-0 border-t-0 border-gray-200 p-4">
        <label class="mb-2 block text-xl font-bold">單號</label>
        <div>
          {{ feedbackResponse.feedback_code }}
        </div>
      </div>
      <div class="rounded-none border border-l-0 border-t-0 border-gray-200 p-4">
        <label class="mb-2 block text-xl font-bold">批號</label>
        <div>
          {{ feedbackResponse.lot_no }}
        </div>
      </div>
      <div class="rounded-none border border-l-0 border-t-0 border-gray-200 p-4">
        <label class="mb-2 block text-xl font-bold">規格</label>
        <div>
          {{ feedbackResponse.product }}
        </div>
      </div>
    </div>

    <div
      v-if="qualityFeedback.id"
      ref="scrollToBottomTable"
      :class="{ 'min-h-[20vh]': caseStatus !== 'closed', 'min-h-[60vh]': caseStatus === 'closed' }"
      class="max-h-[38vh] flex-auto overflow-y-auto overflow-x-hidden md:max-h-none md:min-h-0 md:w-full md:overflow-visible"
    >
      <table
        class="w-full whitespace-nowrap border-b border-solid border-b-gray-200 text-center align-middle text-lg md:min-w-[200px]"
      >
        <thead class="sticky top-0 z-[100] md:hidden">
          <tr class="bg-slate-200 text-xl text-black">
            <th class="w-3/12 p-4 text-left">單位</th>
            <th class="w-1/2 p-4 text-left">內容</th>
            <th class="w-1/12 p-4 text-left">時間</th>
            <th class="w-2/12 p-4 text-left">備註</th>
          </tr>
        </thead>
        <tbody class="md:flex md:flex-col">
          <tr
            v-for="(record, index) in records"
            :key="`records-${index}`"
            class="border-b-2 border-gray-200 hover:bg-gray-200 md:grid md:grid-cols-3"
          >
            <td data-title="單位" class="px-4 py-2 text-left md:col-span-3">
              {{ record.responser.company }} {{ record.responser.name }}
            </td>
            <td data-title="內容" class="px-4 py-2 text-left md:col-span-3">
              <div class="flex items-center whitespace-pre-wrap">{{ record.content }}</div>
            </td>
            <td data-title="時間" class="px-4 py-2 text-left md:col-span-3">
              {{ record.response_at }}
            </td>
            <td data-title="備註" class="flex h-full px-4 py-2 text-left md:col-span-3">
              <div
                v-for="(file, index) in record.file_infos"
                :key="`file-${index}`"
                class="mr-4 flex items-center"
              >
                <a
                  :href="file.url"
                  download
                  class="tooltip tooltip-right text-lg text-scp-royal-blue"
                  :data-tip="file.name"
                  ><i class="icon-report z-10 md:z-0"></i
                ></a>
              </div>
              <a
                v-if="record.category === 'data'"
                :href="`/reports/${record.report_id}`"
                class="tooltip tooltip-right text-lg text-scp-royal-blue"
                :data-tip="record.report_name"
                target="_blank"
              >
                <i class="icon-report z-10 md:z-0"> </i
              ></a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <form v-if="caseStatus !== 'closed'" @submit.prevent="submitForm">
      <div class="mb-3">
        <div class="mt-6 grid grid-cols-2 gap-4 md:grid-cols-1">
          <div>
            <div class="flex items-baseline gap-1 py-1">
              <span class="text-lg font-bold">
                回應內容
                <span class="text-sm font-light text-red-400">*</span>
              </span>
            </div>
            <textarea
              name="content"
              :value="feedbackResponse.response.content"
              @change="feedbackResponse.response.content = $event.target.value"
              class="w-full rounded-md border"
              cols="30"
              rows="5"
              maxlength="500"
              required
            ></textarea>
          </div>
          <div class="py-1">
            <BaseFileUpload
              :label="'附檔'"
              :projectFileList="feedbackResponse.response.upload_files"
              :readonly="false"
              :maxFileNum="3"
              :showUploadedFile="false"
              class="col-span-2"
              @uploadFile="feedbackResponse.response.upload_files = $event"
              @deletePreviewFile="handleDeletePreviewFile"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center justify-end gap-3 py-3">
        <BackButton
          :hasForm="
            feedbackResponse.response.content.trim().length !== 0 ||
            feedbackResponse.response.upload_files.length !== 0
          "
          :returnPath="
            companyType === 'Customer'
              ? '/customer/quality_feedbacks'
              : '/supplier/quality_feedbacks'
          "
        />
        <button type="submit" class="btn btn-success text-xl text-white">
          <SendOutlined />
          送出
        </button>
      </div>
    </form>
    <div v-else class="flex items-center justify-end gap-3 py-3">
      <BackButton
        :returnPath="
          companyType === 'Customer' ? '/customer/quality_feedbacks' : '/supplier/quality_feedbacks'
        "
      />
    </div>
  </div>
</template>
